import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldCheck, FileCheck, BadgeCheck, RotateCcw, Wrench } from "lucide-react";
import { ANTIVIRUS_PLANS, RESELLER } from "@/data/content";

export default function Antivirus() {
  const navigate = useNavigate();

  const go = (plan) => {
    const amount = plan.custom ? "" : plan.price;
    const label = plan.custom ? "Antivirus — Custom Amount" : `Antivirus — ${plan.name}`;
    navigate(`/checkout?plan=${encodeURIComponent(label)}${amount ? `&amount=${amount}` : ""}`);
  };

  return (
    <section id="antivirus" className="bg-gradient-to-b from-[#1B2C4E] to-[#172441] text-white border-y border-white/10" data-testid="antivirus-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="flex flex-col items-start gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-[#3B82F6]/15 border border-[#3B82F6]/30 px-4 py-1.5 text-xs font-semibold text-[#60A5FA]"
                data-testid="reseller-badge"
              >
                <ShieldCheck className="h-4 w-4" /> {RESELLER.text}
              </span>
              <a
                href="/avs-reseller-certificate.pdf"
                target="_blank"
                rel="noreferrer"
                className="btn-sweep inline-flex items-center gap-2 rounded-full bg-[#3B82F6] text-white text-xs font-bold px-4 py-2 shadow-[0_4px_14px_rgba(59,130,246,0.4)] hover:bg-[#2563EB] transition-colors"
                data-testid="reseller-cert-link"
              >
                <FileCheck className="h-4 w-4" /> View our reseller certificate
              </a>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">Security &amp; Antivirus</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase max-w-3xl leading-[0.95]">
              Genuine antivirus protection, activated for you
            </h2>
            <p className="text-white/60 max-w-2xl leading-relaxed">
              Buy genuine, licensed antivirus software through Radio App Consultancy — an authorized reseller. One-time payment,
              free setup &amp; activation help, and a 15-day guarantee. We are an independent reseller and are not
              affiliated with any brand.
            </p>
          </div>
          <img
            src="/reseller-seal.jpg"
            alt="Authorized Reseller since 2019 verified seal"
            className="h-32 w-32 sm:h-40 sm:w-40 rounded-full shrink-0 shadow-xl ring-4 ring-white/10 self-start lg:self-auto"
            data-testid="reseller-seal-img"
          />
        </div>

        <div className="mt-12 rounded-2xl bg-white/[0.04] border border-white/10 px-6 py-6" data-testid="antivirus-brands-strip">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#60A5FA]">Genuine licensed software from leading cyber security brands</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {["Norton", "McAfee", "Bitdefender", "F-Secure", "ESET"].map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-4 py-2.5 text-sm font-semibold text-white/90 hover:border-[#3B82F6]/50 hover:bg-white/[0.1] transition-colors"
                data-testid={`brand-${b.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                <ShieldCheck className="h-4 w-4 text-[#60A5FA]" /> {b}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-4">All trademarks and brand names are the property of their respective owners. We are an independent authorized reseller.</p>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="antivirus-trust-line">
          {[
            { icon: BadgeCheck, title: "100% genuine licenses", text: "Only authentic, fully-licensed software — activated for you." },
            { icon: RotateCcw, title: "7-day money-back", text: "Not activated yet? Get a full refund within 7 days." },
            { icon: Wrench, title: "Free setup & activation", text: "We install and activate it on your devices — no extra charge." },
          ].map((t) => (
            <div key={t.title} className="flex items-start gap-3 rounded-2xl bg-white/[0.04] border border-white/10 px-5 py-4">
              <t.icon className="h-5 w-5 text-[#60A5FA] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-white">{t.title}</p>
                <p className="text-xs text-white/55 mt-0.5 leading-relaxed">{t.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10 items-stretch">
          {ANTIVIRUS_PLANS.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className={`relative rounded-2xl p-7 flex flex-col transition-all duration-300 ${
                plan.featured
                  ? "bg-[#3B82F6] text-[#0F172A] shadow-2xl lg:scale-[1.04]"
                  : "bg-white/[0.04] text-white border border-white/10 hover:border-[#3B82F6] hover:bg-white/[0.07]"
              }`}
              data-testid={`antivirus-card-${plan.id}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold uppercase tracking-[0.15em] ${plan.featured ? "text-[#0F172A]/70" : "text-white/60"}`}>
                  {plan.name}
                </span>
                {plan.badge && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.featured ? "bg-[#26395F] text-[#3B82F6]" : "bg-[#3B82F6]/15 text-[#60A5FA]"}`}>
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="mt-5 flex items-end gap-2">
                {plan.custom ? (
                  <span className="font-display text-4xl tracking-tight">Custom</span>
                ) : (
                  <>
                    <span className="font-display text-5xl tracking-tight">${plan.price}</span>
                    <span className={`mb-2 text-sm font-semibold ${plan.featured ? "text-[#0F172A]/70" : "text-[#60A5FA]"}`}>one-time</span>
                  </>
                )}
              </div>
              <p className={`text-xs mt-2 ${plan.featured ? "text-[#0F172A]/70" : "text-white/55"}`}>{plan.unit}</p>

              <ul className="mt-6 space-y-2.5 flex-1">
                {plan.perks.map((perk) => (
                  <li key={perk} className={`flex items-start gap-2.5 text-sm ${plan.featured ? "text-[#0F172A]/90" : "text-white/80"}`}>
                    <Check className={`h-4 w-4 shrink-0 mt-0.5 ${plan.featured ? "text-[#0F172A]" : "text-[#60A5FA]"}`} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => go(plan)}
                className={`btn-sweep mt-7 w-full font-bold py-3 rounded-full transition-all duration-300 ${
                  plan.featured
                    ? "bg-[#26395F] text-white hover:opacity-90"
                    : "bg-[#3B82F6] text-[#0F172A] hover:glow-primary"
                }`}
                data-testid={`antivirus-buy-${plan.id}`}
              >
                {plan.custom ? "Enter your amount" : "Buy now"}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="text-xs text-white/40 mt-10 max-w-4xl leading-relaxed">
          Radio App Consultancy (a part of Savi Services) is an authorized reseller of AntivirusSales.com, LLC software offerings
          since 2019. All antivirus products are genuine, licensed software. All charges are one-time. We are an
          independent reseller and are not affiliated with, endorsed by, or sponsored by any antivirus brand. All
          product names, logos, and trademarks are the property of their respective owners.
        </p>
      </div>
    </section>
  );
}
